import axios from 'axios';
import { API_URL } from '../components/config';

// JWT Service for Dashboard
class JWTService {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
    this.tokenRefreshTimer = null;
    
    // Setup axios interceptors
    this.setupAxiosInterceptors();
  }

  // Initialize JWT service
  initialize() {
    if (this.accessToken && this.refreshToken && this.user) {
      this.startTokenRefreshTimer();
      console.log('[JWT] Dashboard initialized from storage');
      return true;
    }
    
    console.log('[JWT] No tokens found in storage');
    return false;
  }

  // Setup axios interceptors for automatic token refresh
  setupAxiosInterceptors() {
    // Request interceptor - add auth token
    axios.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle token refresh
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // If error is 401 (Unauthorized) and we have a refresh token
        if (error.response?.status === 401 && 
            error.response?.data?.error === 'INVALID_TOKEN' && 
            this.refreshToken && 
            !originalRequest._retry) {
          
          console.log('[JWT] Token expired, attempting refresh');
          
          // Mark request as retried to prevent infinite loops
          originalRequest._retry = true;
          
          try {
            await this.refreshAccessToken();
            
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${this.accessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error('[JWT] Token refresh failed:', refreshError);
            this.logout();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Login user
  async login(email, password, accountType = null) {
    try {
      console.log('[JWT] Dashboard login attempt:', { email, accountType });

      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
        accountType
      });

      if (response.data.success) {
        // Store tokens and user data
        this.accessToken = response.data.accessToken;
        this.refreshToken = response.data.refreshToken;
        this.user = response.data.user;

        // Save to localStorage
        localStorage.setItem('accessToken', this.accessToken);
        localStorage.setItem('refreshToken', this.refreshToken);
        localStorage.setItem('user', JSON.stringify(this.user));

        // Start token refresh timer
        this.startTokenRefreshTimer();

        console.log('[JWT] Dashboard login successful');
        return response.data;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('[JWT] Dashboard login error:', error);
      throw error;
    }
  }

  // Register new user
  async register(userData) {
    try {
      console.log('[JWT] Dashboard register attempt:', userData.email);

      const response = await axios.post(`${API_URL}/auth/register`, userData);

      if (response.data.success) {
        // Store tokens and user data
        this.accessToken = response.data.accessToken;
        this.refreshToken = response.data.refreshToken;
        this.user = response.data.user;

        // Save to localStorage
        localStorage.setItem('accessToken', this.accessToken);
        localStorage.setItem('refreshToken', this.refreshToken);
        localStorage.setItem('user', JSON.stringify(this.user));

        // Start token refresh timer
        this.startTokenRefreshTimer();

        console.log('[JWT] Dashboard registration successful');
        return response.data;
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('[JWT] Dashboard registration error:', error);
      throw error;
    }
  }

  // Refresh access token
  async refreshAccessToken() {
    try {
      if (!this.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refreshToken: this.refreshToken
      });

      if (response.data.success) {
        this.accessToken = response.data.accessToken;
        this.user = response.data.user;

        // Update localStorage
        localStorage.setItem('accessToken', this.accessToken);
        localStorage.setItem('user', JSON.stringify(this.user));

        console.log('[JWT] Dashboard token refresh successful');
        return response.data;
      } else {
        throw new Error(response.data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('[JWT] Dashboard token refresh error:', error);
      // If refresh fails, logout user
      this.logout();
      throw error;
    }
  }

  // Start automatic token refresh timer
  startTokenRefreshTimer() {
    // Clear existing timer
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }

    // Set timer to refresh token every 23 hours (before 24h expiry)
    this.tokenRefreshTimer = setInterval(async () => {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        console.error('[JWT] Dashboard auto refresh failed:', error);
        // If auto refresh fails, logout user
        this.logout();
      }
    }, 23 * 60 * 60 * 1000); // 23 hours

    console.log('[JWT] Dashboard token refresh timer started');
  }

  // Logout user
  async logout() {
    try {
      console.log('[JWT] Dashboard logging out');

      // Call backend logout if tokens exist
      if (this.accessToken && this.refreshToken) {
        try {
          await axios.post(`${API_URL}/auth/logout`, {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken
          }, {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`
            }
          });
        } catch (error) {
          console.error('[JWT] Dashboard backend logout error:', error);
          // Continue with local logout even if backend fails
        }
      }

      // Clear tokens and user data
      this.accessToken = null;
      this.refreshToken = null;
      this.user = null;

      // Clear timer
      if (this.tokenRefreshTimer) {
        clearInterval(this.tokenRefreshTimer);
        this.tokenRefreshTimer = null;
      }

      // Clear localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      console.log('[JWT] Dashboard logout successful');
      return { success: true };
    } catch (error) {
      console.error('[JWT] Dashboard logout error:', error);
      // Even if logout fails, clear local data
      this.accessToken = null;
      this.refreshToken = null;
      this.user = null;
      
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      return { success: true };
    }
  }

  // Get current user
  getUser() {
    return this.user;
  }

  // Get access token
  getAccessToken() {
    return this.accessToken;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.accessToken && !!this.user;
  }

  // Check if user has specific role
  hasRole(role) {
    return this.user && this.user.role === role;
  }

  // Check if user is admin
  isAdmin() {
    return this.hasRole('Admin') || this.hasRole('SuperAdmin');
  }

  // Get user ID
  getUserId() {
    return this.user ? this.user.userid : null;
  }

  // Get user email
  getUserEmail() {
    return this.user ? this.user.email : null;
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      if (!this.accessToken) {
        throw new Error('Not authenticated');
      }

      console.log('[JWT] Dashboard updating profile:', profileData);

      const response = await axios.put(`${API_URL}/auth/profile`, profileData);

      if (response.data.success) {
        this.user = response.data.user;
        localStorage.setItem('user', JSON.stringify(this.user));
        
        console.log('[JWT] Dashboard profile update successful');
        return response.data;
      } else {
        throw new Error(response.data.message || 'Profile update failed');
      }
    } catch (error) {
      console.error('[JWT] Dashboard profile update error:', error);
      throw error;
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword, confirmPassword) {
    try {
      if (!this.accessToken) {
        throw new Error('Not authenticated');
      }

      console.log('[JWT] Dashboard changing password');

      const response = await axios.post(`${API_URL}/auth/change-password`, {
        currentPassword,
        newPassword,
        confirmPassword
      });

      if (response.data.success) {
        console.log('[JWT] Dashboard password change successful');
        return response.data;
      } else {
        throw new Error(response.data.message || 'Password change failed');
      }
    } catch (error) {
      console.error('[JWT] Dashboard password change error:', error);
      throw error;
    }
  }

  // Get current user profile
  async getProfile() {
    try {
      if (!this.accessToken) {
        throw new Error('Not authenticated');
      }

      const response = await axios.get(`${API_URL}/auth/profile`);

      if (response.data.success) {
        this.user = response.data.user;
        localStorage.setItem('user', JSON.stringify(this.user));
        
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to get profile');
      }
    } catch (error) {
      console.error('[JWT] Dashboard get profile error:', error);
      throw error;
    }
  }

  // Clear all data (for debugging/testing)
  clearAllData() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;

    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    console.log('[JWT] Dashboard all data cleared');
  }
}

// Create singleton instance
const jwtService = new JWTService();

export default jwtService;
